import React, { useState, useEffect } from 'react';
import { X, Loader, Upload, Trash2 } from 'lucide-react';
import { peopleService } from './peopleService';
import { uploadService } from '../../lib/uploadService';
import { useAuth } from '../../hooks/useAuth';
import { Person } from '../../types';
import { SkillChipInput } from './SkillChipInput';

interface EditPersonModalProps {
  isOpen: boolean;
  person: Person | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditPersonModal: React.FC<EditPersonModalProps> = ({ isOpen, person, onClose, onSuccess }) => {
  const { user, permissions } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [existingSkills, setExistingSkills] = useState<{ [category: string]: string[] }>({});
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    title: '',
    department: '',
    phone: '',
    bio: '',
    arbeidsoppgaver: '',
    verktoyOgSystemer: [] as string[],
    spesialiserteVerktoyUtvikler: [] as string[],
    spesialiserteVerktoyDesigner: [] as string[],
    spesialiserteVerktoyMarkedsforing: [] as string[],
    spesialiserteVerktoyProsjektledelse: [] as string[],
    spesialiserteVerktoy3D: [] as string[],
    spesialiserteVerktoyAI: [] as string[],
    andreFerdigheter: ''
  });
  
  const [error, setError] = useState('');

  // Load existing skills when modal opens
  useEffect(() => {
    if (isOpen) {
      loadExistingSkills();
    }
  }, [isOpen]);

  const loadExistingSkills = async () => {
    try {
      const people = await peopleService.getPeople();
      const skillCategories = {
        verktoyOgSystemer: new Set<string>(),
        spesialiserteVerktoyUtvikler: new Set<string>(),
        spesialiserteVerktoyDesigner: new Set<string>(),
        spesialiserteVerktoyMarkedsforing: new Set<string>(),
        spesialiserteVerktoyProsjektledelse: new Set<string>(),
        spesialiserteVerktoy3D: new Set<string>(),
        spesialiserteVerktoyAI: new Set<string>(),
      };

      people.forEach(person => {
        Object.keys(skillCategories).forEach(category => {
          const skills = person[category as keyof Person] as string[] || [];
          skills.forEach(skill => {
            if (skill.trim()) {
              skillCategories[category as keyof typeof skillCategories].add(skill.trim());
            }
          });
        });
      });

      const skillsSorted = Object.fromEntries(
        Object.entries(skillCategories).map(([category, skillSet]) => [
          category, 
          Array.from(skillSet).sort()
        ])
      );

      setExistingSkills(skillsSorted);
    } catch (error) {
      console.error('Failed to load existing skills:', error);
    }
  };

  // Populate form when person changes
  useEffect(() => {
    if (person) {
      setFormData({
        email: person.email,
        name: person.name,
        title: person.title,
        department: person.department,
        phone: person.phone,
        bio: person.bio,
        arbeidsoppgaver: person.arbeidsoppgaver,
        verktoyOgSystemer: [...person.verktoyOgSystemer],
        spesialiserteVerktoyUtvikler: [...person.spesialiserteVerktoyUtvikler],
        spesialiserteVerktoyDesigner: [...person.spesialiserteVerktoyDesigner],
        spesialiserteVerktoyMarkedsforing: [...person.spesialiserteVerktoyMarkedsforing],
        spesialiserteVerktoyProsjektledelse: [...person.spesialiserteVerktoyProsjektledelse],
        spesialiserteVerktoy3D: [...person.spesialiserteVerktoy3D],
        spesialiserteVerktoyAI: [...person.spesialiserteVerktoyAI],
        andreFerdigheter: person.andreFerdigheter
      });
      setProfilePreview(person.profilePictureUrl || null);
      setRemoveImage(false);
      setShowDeleteConfirm(false);
    }
  }, [person]);

  // Check if current user can delete this person
  const canDeletePerson = (): boolean => {
    if (!user || !person) return false;
    // Only admins/owners can delete people, and they can't delete themselves
    return permissions.canManageUsers && user.email !== person.email;
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate the image file
    const validation = uploadService.validateProfileImage(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid image file');
      return;
    }

    setProfileImage(file);
    setRemoveImage(false);
    setError('');
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setProfilePreview(previewUrl);
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setProfilePreview(null);
    setRemoveImage(true);
  };

  const handleDelete = async () => {
    if (!person || !user) return;

    setDeleting(true);
    setError('');

    try {
      await peopleService.deletePerson(person.id);
      onSuccess();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Failed to delete person');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !person) return;

    if (!formData.email.trim() || !formData.name.trim()) {
      setError('Email and name are required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      let profilePictureUrl = person.profilePictureUrl;
      let profilePicturePath = person.profilePicturePath;

      // Handle image removal
      if (removeImage) {
        profilePictureUrl = '';
        profilePicturePath = '';
      }
      // Upload new profile image if one was selected
      else if (profileImage) {
        try {
          console.log('Uploading new profile image...');
          const uploadResult = await uploadService.uploadProfileImage(profileImage);
          profilePictureUrl = uploadResult.url;
          profilePicturePath = uploadResult.path;
          console.log('Profile image uploaded successfully:', profilePictureUrl);
        } catch (uploadError: any) {
          console.error('Failed to upload profile image:', uploadError);
          setError(`Failed to upload image: ${uploadError.message}`);
          setSubmitting(false);
          return;
        }
      }

      // Skills are already arrays, no conversion needed
      const skillsData = {
        verktoyOgSystemer: formData.verktoyOgSystemer,
        spesialiserteVerktoyUtvikler: formData.spesialiserteVerktoyUtvikler,
        spesialiserteVerktoyDesigner: formData.spesialiserteVerktoyDesigner,
        spesialiserteVerktoyMarkedsforing: formData.spesialiserteVerktoyMarkedsforing,
        spesialiserteVerktoyProsjektledelse: formData.spesialiserteVerktoyProsjektledelse,
        spesialiserteVerktoy3D: formData.spesialiserteVerktoy3D,
        spesialiserteVerktoyAI: formData.spesialiserteVerktoyAI,
      };

      await peopleService.updatePerson(person.id, {
        email: formData.email.trim(),
        name: formData.name.trim(),
        title: formData.title.trim(),
        department: formData.department.trim(),
        phone: formData.phone.trim(),
        profilePictureUrl,
        profilePicturePath,
        bio: formData.bio.trim(),
        arbeidsoppgaver: formData.arbeidsoppgaver.trim(),
        andreFerdigheter: formData.andreFerdigheter.trim(),
        ...skillsData
      });

      // Reset and close modal
      setProfileImage(null);
      onSuccess();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Failed to update person');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting || deleting) return;
    setProfileImage(null);
    setProfilePreview(person?.profilePictureUrl || null);
    setRemoveImage(false);
    setShowDeleteConfirm(false);
    setError('');
    onClose();
  };

  if (!isOpen || !person) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Edit Profile: {person.name}</h2>
            <div className="flex items-center gap-2">
              {/* Delete Button - Only show if user can delete */}
              {canDeletePerson() && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={submitting || deleting}
                  className="text-red-600 hover:text-red-800 disabled:opacity-50 p-2 rounded-lg hover:bg-red-50 transition-colors"
                  title="Delete Person"
                >
                  <Trash2 size={20} />
                </button>
              )}
              <button
                onClick={handleClose}
                disabled={submitting || deleting}
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Delete Confirmation */}
          {showDeleteConfirm && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="text-red-800 font-semibold mb-2">Delete {person.name}?</h3>
              <p className="text-red-700 text-sm mb-4">
                This action cannot be undone. All profile information and associated data will be permanently deleted.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {deleting ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete Person
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Image Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">Profile Picture</label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div 
                    onClick={() => document.getElementById('edit-profile-image')?.click()}
                    className="w-20 h-20 rounded-full cursor-pointer hover:opacity-80 transition-opacity border-2 border-gray-200"
                  >
                    {profilePreview ? (
                      <img
                        src={profilePreview}
                        alt="Profile preview"
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors">
                        <Upload size={24} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Remove Image Button */}
                  {profilePreview && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                
                <div>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="edit-profile-image"
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById('edit-profile-image')?.click()}
                    className="bg-secondary text-secondary-foreground px-4 py-2 rounded-lg hover:bg-secondary/80 transition-colors border"
                  >
                    Change Image
                  </button>
                  <p className="text-xs text-muted-foreground mt-1">
                    Optional. JPG, PNG, or WebP up to 5MB. Click avatar or button to select. Click X to remove.
                  </p>
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div className="space-y-4">
              {/* Full Name - Full width, first */}
              <div>
                <label className="block text-sm font-medium mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 border rounded-lg"
                  placeholder="John Doe"
                  required
                />
              </div>

              {/* Job Title and Department */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Job Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full p-3 border rounded-lg"
                    placeholder="Senior Designer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                    className="w-full p-3 border rounded-lg"
                    placeholder="Oslo, Tønsberg, Stavanger"
                  />
                </div>
              </div>

              {/* Email and Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full p-3 border rounded-lg"
                    placeholder="employee@agency.com"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This links to their user account
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full p-3 border rounded-lg"
                    placeholder="+47 123 45 678"
                  />
                </div>
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium mb-2">Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                className="w-full p-3 border rounded-lg h-24"
                placeholder="Brief professional bio..."
              />
            </div>

            {/* Skills Sections */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Skills & Tools</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2">Work Tasks</label>
                <textarea
                  value={formData.arbeidsoppgaver}
                  onChange={(e) => setFormData(prev => ({ ...prev, arbeidsoppgaver: e.target.value }))}
                  className="w-full p-3 border rounded-lg h-20"
                  placeholder="Describe main work tasks and responsibilities..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SkillChipInput
                  label="Tools and Systems"
                  skills={formData.verktoyOgSystemer}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, verktoyOgSystemer: skills }))}
                  existingSkills={existingSkills.verktoyOgSystemer || []}
                  placeholder="Slack, Google Workspace, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: Developer"
                  skills={formData.spesialiserteVerktoyUtvikler}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyUtvikler: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyUtvikler || []}
                  placeholder="React, Node.js, Python, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: Designer"
                  skills={formData.spesialiserteVerktoyDesigner}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyDesigner: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyDesigner || []}
                  placeholder="Figma, Adobe Creative Suite, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: Marketing"
                  skills={formData.spesialiserteVerktoyMarkedsforing}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyMarkedsforing: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyMarkedsforing || []}
                  placeholder="Google Ads, Facebook Ads, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: Project Management"
                  skills={formData.spesialiserteVerktoyProsjektledelse}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyProsjektledelse: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyProsjektledelse || []}
                  placeholder="Asana, Jira, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: 3D"
                  skills={formData.spesialiserteVerktoy3D}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoy3D: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoy3D || []}
                  placeholder="Blender, Cinema 4D, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: AI"
                  skills={formData.spesialiserteVerktoyAI}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyAI: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyAI || []}
                  placeholder="ChatGPT, Midjourney, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Other Skills</label>
                <textarea
                  value={formData.andreFerdigheter}
                  onChange={(e) => setFormData(prev => ({ ...prev, andreFerdigheter: e.target.value }))}
                  className="w-full p-3 border rounded-lg h-20"
                  placeholder="Other skills, certifications, languages, etc."
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting || deleting}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-accent disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.email.trim() || !formData.name.trim() || submitting || deleting || showDeleteConfirm}
                className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg border border-primary flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    {profileImage ? 'Uploading image...' : 'Updating...'}
                  </>
                ) : (
                  'Update Profile'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};